import type { ReactNode } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import type {
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form/dist/types/form';
import type { IntlShape } from 'react-intl/src/types';

import { commonErrorCodeMessages } from '@/errorCodes/common';
import { mapApiErrorCode } from '@/errorCodes/helpers';
import type { RequireAtLeastOne } from '@/utils/tsutils';

import type { ApiError, ApiErrorType } from '@/lib/types';
import { formMessages } from '@/messages/forms';

import { GENERIC_API_FIELDS } from './const';

export const generateFieldPrefix =
  <V extends FieldValues>(schemaPrefix: string | undefined) =>
  (name: string) =>
    (schemaPrefix ? `${schemaPrefix}.${name}` : name) as FieldPath<V>;

/**
 * Inject Saleor Api errors with error.field present into a related form.
 */
export type GenericErrorHandler = ReturnType<typeof genericErrorHandler>;
export const genericErrorHandler =
  <V extends FieldValues = FieldValues>(
    initOpts: RequireAtLeastOne<{
      apiErrorsHandler: (errors: ApiError[], errorType: ApiErrorType) => void;
      errorType?: ApiErrorType;
      formErrorsHandler: UseFormSetError<V>;
    }> & { formatMessage: IntlShape['formatMessage'] },
  ) =>
  (
    errors: ApiError[],
    opts?: {
      errorType?: ApiErrorType;
      fieldsMapping?: Record<string, string[]>;
      genericApiFields?: string[];
      schemaPrefix?: string;
    },
  ) => {
    const {
      apiErrorsHandler,
      errorType: baseErrorType,
      formErrorsHandler,
      formatMessage,
    } = initOpts;
    const { errorType, fieldsMapping, genericApiFields, schemaPrefix } = {
      genericApiFields: [],
      errorType: baseErrorType,
      ...opts,
    };
    const computedGenericApiFields = [
      ...genericApiFields,
      ...GENERIC_API_FIELDS,
    ];
    const withSchemaPrefix = generateFieldPrefix<V>(schemaPrefix);

    const formErrors = errors.filter(({ field }) => field);
    const apiErrors = errors.filter(
      ({ field }) =>
        !field ||
        field === '__root__' ||
        computedGenericApiFields?.includes(field),
    );

    if (!errorType) {
      throw new Error('No error type passed!');
    }

    if (formErrorsHandler) {
      formErrors.forEach((error) => {
        const { field } = error;
        const mappedMessage = errorType
          ? mapApiErrorCode(errorType, error)
          : null;
        const message = (() => {
          if (!mappedMessage?.message) {
            return formatMessage(
              commonErrorCodeMessages.unexpectedErrorOccurred,
            );
          }
          if (typeof mappedMessage.message === 'string') {
            return mappedMessage.message;
          }
          return formatMessage(mappedMessage.message, mappedMessage.values);
        })();

        if (fieldsMapping?.[field!]) {
          const mappedField = fieldsMapping[field!];
          mappedField.forEach((mappedField) =>
            formErrorsHandler(withSchemaPrefix(mappedField), { message }),
          );
        } else {
          formErrorsHandler(withSchemaPrefix(field!), { message });
        }
      });
    }

    if (apiErrorsHandler && apiErrors?.length) {
      apiErrorsHandler(apiErrors, errorType);
    }
  };

export const getPlaceholderSuffix = (
  placeholder: ReactNode,
  required: boolean | undefined,
  formatMessage: IntlShape['formatMessage'],
) => {
  let labelAddition = '';

  if (typeof required === 'boolean') {
    if (required) {
      labelAddition = '*';
    } else {
      labelAddition = ` (${formatMessage(formMessages.optional)})`;
    }
  }

  if (placeholder) {
    if (typeof placeholder === 'string') {
      return `${placeholder}${labelAddition}`;
    } else {
      return (
        <>
          {placeholder}
          {labelAddition}
        </>
      );
    }
  }

  return undefined;
};

export const getPhonePrefix = (
  phonePrefixes: string[],
  shippingPhone?: string | null,
) =>
  phonePrefixes.find((prefix) => shippingPhone?.startsWith(`+${prefix}`)) ||
  phonePrefixes[0];

export const getPhone = (phonePrefix?: string, shippingPhone?: string | null) =>
  shippingPhone ? shippingPhone.replace(`+${phonePrefix}`, '') : '';

export const getPhonePrefixesChoices = (phonePrefixes: string[]) =>
  phonePrefixes.map((phonePrefix) => ({
    label: `+${phonePrefix}`,
    value: phonePrefix,
  }));

export const setDefaultValuesByFormContextSetter = (
  defaultValues: Record<string, string>,
  setter: UseFormSetValue<any>,
) => {
  Object.keys(defaultValues).forEach((key) => {
    setter(key, defaultValues[key]);
  });
};
